import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  ExpandableSection,
  Form,
  FormGroup,
  FormHelperText,
  FormSelect,
  FormSelectOption,
  HelperText,
  HelperTextItem,
  Label,
  LabelGroup,
  MenuToggle,
  MenuToggleElement,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  NumberInput,
  Select,
  SelectList,
  SelectOption,
  TextInput,
  TextInputGroup,
  TextInputGroupMain,
  TextInputGroupUtilities,
} from '@patternfly/react-core';
import TimesIcon from '@patternfly/react-icons/dist/esm/icons/times-icon';
import { countries } from 'countries-list';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type { Certificate } from '../../../types/models/Certificate';
import type { ApiResponseCertificateCreate } from '../../../types/api/ApiResponseCertificate';

interface CreateCertificateModalProps {
  isOpen: boolean;
  platformId: number;
  platformName: string;
  onClose: () => void;
  onCreated: (certificate: Certificate) => void;
}

type KeyAlgorithm = 'rsa' | 'ec';
type RsaSize = '2048' | '3072' | '4096' | '8192';
type EcCurve = 'prime256v1' | 'secp384r1' | 'secp521r1';
type Digest = 'sha256' | 'sha384' | 'sha512';
type CertType = 'usr_cert' | 'server_cert' | 'combined_server_client';

interface FormState {
  description: string;
  commonName: string;
  country: string;
  lifetimeDays: number | '';
  certType: CertType;
  keyAlgorithm: KeyAlgorithm;
  rsaSize: RsaSize;
  ecCurve: EcCurve;
  digest: Digest;
  state: string;
  city: string;
  organization: string;
  organizationalUnit: string;
  email: string;
  sansDns: string[];
  sansIp: string[];
  sansUri: string[];
  sansEmail: string[];
}

const INITIAL_STATE: FormState = {
  description: '',
  commonName: '',
  country: 'BE',
  lifetimeDays: 397,
  certType: 'usr_cert',
  keyAlgorithm: 'ec',
  rsaSize: '2048',
  ecCurve: 'prime256v1',
  digest: 'sha256',
  state: '',
  city: '',
  organization: '',
  organizationalUnit: '',
  email: '',
  sansDns: [],
  sansIp: [],
  sansUri: [],
  sansEmail: [],
};

interface CountryEntry {
  code: string;
  name: string;
}

// Built once from the bundled `countries-list` package — keeps us out of the
// business of hand-maintaining an ISO list and stays in sync with the upstream
// data as the dependency updates.
const COUNTRIES: readonly CountryEntry[] = Object.entries(countries)
  .map(([code, c]) => ({ code, name: c.name }))
  .sort((a, b) => a.name.localeCompare(b.name));

const EMAIL_RE = /^\S+@\S+\.\S+$/;
const IPV4_RE = /^(\d{1,3}\.){3}\d{1,3}$/;
const IPV6_RE = /^[0-9a-fA-F:]+$/;
// Mirrors PHP's FILTER_VALIDATE_DOMAIN | FILTER_FLAG_HOSTNAME. Backend is authoritative.
const HOSTNAME_LABEL_RE = /^(?!-)[A-Za-z0-9-]{1,63}(?<!-)$/;
const COUNTRY_RE = /^[A-Z]{2}$/;

function looksLikeIp(value: string): boolean {
  return IPV4_RE.test(value) || (IPV6_RE.test(value) && value.includes(':'));
}

export function looksLikeHostname(value: string): boolean {
  if (!value.includes('.')) {
    return false;
  }
  const labels = value.split('.');
  if (labels.some((label) => label.length === 0)) {
    return false;
  }
  return labels.every((label) => HOSTNAME_LABEL_RE.test(label));
}

interface ChipListFieldProps {
  label: string;
  fieldId: string;
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  validateEntry: (value: string) => string | null;
}

// Shared chip-style list input (SAN lists here, the domain list in the ZeroSSL
// order modal): type a value, Enter/blur commits it through `validateEntry`.
export function ChipListField({ label, fieldId, values, onChange, placeholder, validateEntry }: ChipListFieldProps) {
  const [buffer, setBuffer] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const commit = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed.length === 0) {
      return;
    }
    const message = validateEntry(trimmed);
    if (message !== null) {
      setError(message);
      return;
    }
    if (values.includes(trimmed)) {
      setBuffer('');
      setError(null);
      return;
    }
    onChange([...values, trimmed]);
    setBuffer('');
    setError(null);
  };

  const inputId = `${fieldId}-input`;

  return (
    <FormGroup label={label} fieldId={inputId}>
      {values.length > 0 && (
        <LabelGroup className="pf-v6-u-mb-sm" aria-label={`${label} entries`}>
          {values.map((value) => (
            <Label
              key={value}
              onClose={() => onChange(values.filter((v) => v !== value))}
              closeBtnAriaLabel={`Remove ${value}`}
            >
              {value}
            </Label>
          ))}
        </LabelGroup>
      )}
      <TextInput
        id={inputId}
        type="text"
        autoComplete="off"
        value={buffer}
        onChange={(_event, value) => {
          setBuffer(value);
          if (error !== null) {
            setError(null);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit(buffer);
          } else if (event.key === 'Backspace' && buffer.length === 0 && values.length > 0) {
            onChange(values.slice(0, -1));
          }
        }}
        onBlur={() => {
          if (buffer.trim().length > 0) {
            commit(buffer);
          }
        }}
        placeholder={placeholder}
        aria-label={label}
      />
      {error !== null && (
        <FormHelperText>
          <HelperText>
            <HelperTextItem variant="error">{error}</HelperTextItem>
          </HelperText>
        </FormHelperText>
      )}
    </FormGroup>
  );
}

const COUNTRY_INPUT_ID = 'create-certificate-country-input';
const COUNTRY_LISTBOX_ID = 'create-certificate-country-listbox';
const COUNTRY_NO_RESULTS = '__no_results__';

interface CountrySelectFieldProps {
  value: string;
  onChange: (code: string) => void;
}

function CountrySelectField({ value, onChange }: CountrySelectFieldProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(() => {
    const country = COUNTRIES.find((c) => c.code === value);
    return country ? `${country.name} (${country.code})` : '';
  });
  const [filterValue, setFilterValue] = useState<string>('');
  const [focusedItemIndex, setFocusedItemIndex] = useState<number | null>(null);
  const textInputRef = useRef<HTMLInputElement | null>(null);

  // Sync the visible input back to the selected country whenever the dropdown
  // closes — typing without committing is treated as "throw away your text"
  // and revert to whichever country is actually stored in `value`.
  useEffect(() => {
    if (!isOpen) {
      const country = COUNTRIES.find((c) => c.code === value);
      setInputValue(country ? `${country.name} (${country.code})` : '');
      setFilterValue('');
      setFocusedItemIndex(null);
    }
  }, [value, isOpen]);

  const filtered = useMemo<CountryEntry[]>(() => {
    const q = filterValue.trim().toLowerCase();
    if (!q) {
      return [...COUNTRIES];
    }
    return COUNTRIES.filter((c) => c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q));
  }, [filterValue]);

  const optionItems: CountryEntry[] =
    filtered.length === 0 ? [{ code: COUNTRY_NO_RESULTS, name: 'No countries match' }] : filtered;

  const closeMenu = () => {
    setIsOpen(false);
    setFocusedItemIndex(null);
  };

  const onSelect = (_event: React.MouseEvent<Element, MouseEvent> | undefined, chosen: string | number | undefined) => {
    if (typeof chosen !== 'string' || chosen === COUNTRY_NO_RESULTS) {
      return;
    }
    const country = COUNTRIES.find((c) => c.code === chosen);
    if (!country) {
      return;
    }
    onChange(country.code);
    setInputValue(`${country.name} (${country.code})`);
    setFilterValue('');
    closeMenu();
  };

  const onTextInputChange = (_event: React.FormEvent<HTMLInputElement>, next: string) => {
    setInputValue(next);
    setFilterValue(next);
    if (!isOpen) {
      setIsOpen(true);
    }
    setFocusedItemIndex(null);
  };

  const handleArrowKeys = (key: 'ArrowUp' | 'ArrowDown') => {
    if (!isOpen) {
      setIsOpen(true);
    }
    if (optionItems.length === 0 || optionItems[0].code === COUNTRY_NO_RESULTS) {
      return;
    }
    const last = optionItems.length - 1;
    if (key === 'ArrowDown') {
      setFocusedItemIndex(focusedItemIndex === null || focusedItemIndex === last ? 0 : focusedItemIndex + 1);
    } else {
      setFocusedItemIndex(focusedItemIndex === null || focusedItemIndex === 0 ? last : focusedItemIndex - 1);
    }
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'Enter': {
        event.preventDefault();
        if (focusedItemIndex !== null) {
          const item = optionItems[focusedItemIndex];
          if (item.code !== COUNTRY_NO_RESULTS) {
            onSelect(undefined, item.code);
          }
        } else if (!isOpen) {
          setIsOpen(true);
        }
        break;
      }
      case 'ArrowUp':
      case 'ArrowDown':
        event.preventDefault();
        handleArrowKeys(event.key);
        break;
      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          closeMenu();
        }
        break;
    }
  };

  const onToggleClick = () => {
    setIsOpen((open) => !open);
    textInputRef.current?.focus();
  };

  const onClearButtonClick = () => {
    onChange('');
    setInputValue('');
    setFilterValue('');
    setFocusedItemIndex(null);
    textInputRef.current?.focus();
  };

  const toggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      variant="typeahead"
      aria-label="Country (ISO 3166-1 alpha-2)"
      onClick={onToggleClick}
      isExpanded={isOpen}
      isFullWidth
    >
      <TextInputGroup isPlain>
        <TextInputGroupMain
          value={inputValue}
          onClick={() => {
            if (!isOpen) {
              setIsOpen(true);
            }
          }}
          onChange={onTextInputChange}
          onKeyDown={onInputKeyDown}
          id={COUNTRY_INPUT_ID}
          autoComplete="off"
          innerRef={textInputRef}
          placeholder="Type a country name or code"
          role="combobox"
          isExpanded={isOpen}
          aria-controls={COUNTRY_LISTBOX_ID}
        />
        <TextInputGroupUtilities {...(!inputValue ? { style: { display: 'none' } } : {})}>
          <Button variant="plain" onClick={onClearButtonClick} aria-label="Clear country" icon={<TimesIcon />} />
        </TextInputGroupUtilities>
      </TextInputGroup>
    </MenuToggle>
  );

  return (
    <Select
      id="create-certificate-country"
      isOpen={isOpen}
      selected={value}
      onSelect={onSelect}
      onOpenChange={(open) => {
        if (!open) {
          closeMenu();
        }
      }}
      toggle={toggle}
      variant="typeahead"
    >
      <SelectList id={COUNTRY_LISTBOX_ID}>
        {optionItems.map((option, index) => (
          <SelectOption
            key={option.code}
            value={option.code}
            isFocused={focusedItemIndex === index}
            isAriaDisabled={option.code === COUNTRY_NO_RESULTS}
          >
            {option.code === COUNTRY_NO_RESULTS ? option.name : `${option.name} (${option.code})`}
          </SelectOption>
        ))}
      </SelectList>
    </Select>
  );
}

function CreateCertificateModal({ isOpen, platformId, platformName, onClose, onCreated }: CreateCertificateModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [formState, setFormState] = useState<FormState>(INITIAL_STATE);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isKeyExpanded, setIsKeyExpanded] = useState<boolean>(false);
  const [isDnExpanded, setIsDnExpanded] = useState<boolean>(false);
  const [isSansExpanded, setIsSansExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      setFormState(INITIAL_STATE);
      setIsSubmitting(false);
      setSubmitError(null);
      setIsKeyExpanded(false);
      setIsDnExpanded(false);
      setIsSansExpanded(false);
    }
  }, [isOpen]);

  const patch = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
    setSubmitError((current) => (current !== null ? null : current));
  }, []);

  const descriptionTrimmed = formState.description.trim();
  const commonNameTrimmed = formState.commonName.trim();
  const emailTrimmed = formState.email.trim();

  const descriptionInvalid = descriptionTrimmed.length === 0;
  const commonNameInvalid = commonNameTrimmed.length === 0 || commonNameTrimmed.length > 64;
  const countryInvalid = !COUNTRY_RE.test(formState.country);
  const lifetimeInvalid =
    typeof formState.lifetimeDays !== 'number' ||
    !Number.isInteger(formState.lifetimeDays) ||
    formState.lifetimeDays < 1 ||
    formState.lifetimeDays > 825;
  const emailInvalid = emailTrimmed.length > 0 && !EMAIL_RE.test(emailTrimmed);

  const hasBlockingValidationError =
    descriptionInvalid || commonNameInvalid || countryInvalid || lifetimeInvalid || emailInvalid;

  const handleLifetimeChange = (event: React.FormEvent<HTMLInputElement>) => {
    const raw = event.currentTarget.value;
    if (raw === '') {
      patch('lifetimeDays', '');
      return;
    }
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) {
      patch('lifetimeDays', parsed);
    }
  };

  const adjustLifetime = (delta: number) => {
    const current = typeof formState.lifetimeDays === 'number' ? formState.lifetimeDays : 397;
    patch('lifetimeDays', Math.min(825, Math.max(1, current + delta)));
  };

  const handleSubmit = useCallback(async () => {
    if (hasBlockingValidationError || typeof formState.lifetimeDays !== 'number') {
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const keyType = formState.keyAlgorithm === 'ec' ? formState.ecCurve : `rsa${formState.rsaSize}`;

      const body = {
        certificate_platform_id: platformId,
        description: descriptionTrimmed,
        common_name: commonNameTrimmed,
        lifetime_days: formState.lifetimeDays,
        cert_type: formState.certType,
        key_type: keyType,
        digest: formState.digest,
        country: formState.country,
        state: formState.state.trim(),
        city: formState.city.trim(),
        organization: formState.organization.trim(),
        organizational_unit: formState.organizationalUnit.trim(),
        email: emailTrimmed,
        sans_dns: formState.sansDns,
        sans_ip: formState.sansIp,
        sans_uri: formState.sansUri,
        sans_email: formState.sansEmail,
      };

      const response = await apiFetch('/datacenters/certificates', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      let responseBody: ApiResponseCertificateCreate | null;
      try {
        responseBody = (await response.json()) as ApiResponseCertificateCreate;
      } catch {
        responseBody = null;
      }

      if (response.ok && responseBody?.status === 'success' && responseBody.data?.certificate) {
        const created = responseBody.data.certificate;
        onCreated(created);
        addNotification({
          title: 'Certificate created',
          srTitle: 'Success alert',
          description: `${created.cn} has been created on ${platformName}.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        setIsSubmitting(false);
        onClose();
        return;
      }

      // Presence of `platform_ref` in the error envelope signals the certificate
      // was issued by the upstream CA but the manager failed to import it — the
      // user must NOT retry, or they will create a duplicate.
      if (!response.ok && responseBody?.data?.platform_ref) {
        addNotification({
          title: 'Certificate created but not imported',
          srTitle: 'Error alert',
          description: `A certificate was created on ${platformName} (reference: ${responseBody.data.platform_ref}) but could not be imported into the manager. It will appear after the next sync. Do NOT retry — this would create a duplicate. Contact support if it does not appear within a few minutes.`,
          variant: 'danger',
        });
        setDrawerOpen(true);
        setIsSubmitting(false);
        onClose();
        return;
      }

      const fallback =
        responseBody?.data?.msg ??
        responseBody?.message ??
        `Request failed with status ${response.status} ${response.statusText}`.trim();
      setSubmitError(fallback);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'An unexpected error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    hasBlockingValidationError,
    formState,
    platformId,
    platformName,
    descriptionTrimmed,
    commonNameTrimmed,
    emailTrimmed,
    apiFetch,
    addNotification,
    setDrawerOpen,
    onCreated,
    onClose,
  ]);

  let commonNameHint: React.ReactNode = null;
  if (commonNameInvalid && commonNameTrimmed.length > 64) {
    commonNameHint = <HelperTextItem variant="error">Common name must be 64 characters or fewer.</HelperTextItem>;
  } else if (commonNameTrimmed.length === 0) {
    commonNameHint = <HelperTextItem>Up to 64 characters.</HelperTextItem>;
  } else if (looksLikeHostname(commonNameTrimmed)) {
    commonNameHint = (
      <HelperTextItem variant="indeterminate">
        <code>{commonNameTrimmed}</code> will be automatically included as a DNS Subject Alternative Name.
      </HelperTextItem>
    );
  }

  return (
    <Modal variant="large" isOpen={isOpen} onClose={onClose} aria-label={`Create certificate on ${platformName}`}>
      <ModalHeader title={`Create certificate on ${platformName}`} />
      <ModalBody>
        {submitError !== null && (
          <Alert variant="danger" isInline title="Could not create certificate" className="pf-v6-u-mb-md">
            {submitError}
          </Alert>
        )}
        <Form>
          <FormGroup label="Description" isRequired fieldId="create-certificate-description">
            <TextInput
              id="create-certificate-description"
              type="text"
              autoComplete="off"
              value={formState.description}
              onChange={(_event, value) => patch('description', value)}
              validated={descriptionInvalid && descriptionTrimmed.length > 0 ? 'error' : 'default'}
              aria-label="Description"
              isRequired
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem>
                  Free-form label, e.g. <code>manager-frontend-tls</code>.
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup label="Common name" isRequired fieldId="create-certificate-cn">
            <TextInput
              id="create-certificate-cn"
              type="text"
              autoComplete="off"
              value={formState.commonName}
              onChange={(_event, value) => patch('commonName', value)}
              validated={commonNameInvalid && commonNameTrimmed.length > 0 ? 'error' : 'default'}
              aria-label="Common name"
              isRequired
            />
            {commonNameHint !== null && (
              <FormHelperText>
                <HelperText>{commonNameHint}</HelperText>
              </FormHelperText>
            )}
          </FormGroup>

          <FormGroup label="Country" isRequired fieldId={COUNTRY_INPUT_ID}>
            <CountrySelectField value={formState.country} onChange={(code) => patch('country', code)} />
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={countryInvalid ? 'error' : 'default'}>
                  ISO 3166-1 alpha-2 country code.
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup label="Lifetime (days)" isRequired fieldId="create-certificate-lifetime">
            <NumberInput
              value={formState.lifetimeDays}
              min={1}
              max={825}
              widthChars={5}
              inputName="lifetimeDays"
              inputAriaLabel="Lifetime in days"
              minusBtnAriaLabel="Decrease lifetime"
              plusBtnAriaLabel="Increase lifetime"
              inputProps={{ id: 'create-certificate-lifetime' }}
              onMinus={() => adjustLifetime(-1)}
              onPlus={() => adjustLifetime(1)}
              onChange={handleLifetimeChange}
              validated={lifetimeInvalid && formState.lifetimeDays !== '' ? 'error' : 'default'}
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={lifetimeInvalid ? 'error' : 'default'}>
                  Between 1 and 825 days. The default of 397 matches the public-CA convention.
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <ExpandableSection
            toggleText="Key"
            isExpanded={isKeyExpanded}
            onToggle={(_event, expanded) => setIsKeyExpanded(expanded)}
          >
            <Form>
              <FormGroup label="Certificate type" fieldId="create-certificate-cert-type">
                <FormSelect
                  id="create-certificate-cert-type"
                  value={formState.certType}
                  onChange={(_event, value) => patch('certType', value as CertType)}
                  aria-label="Certificate type"
                >
                  <FormSelectOption value="usr_cert" label="Client (usr_cert)" />
                  <FormSelectOption value="server_cert" label="Server (server_cert)" />
                  <FormSelectOption value="combined_server_client" label="Client + Server (combined_server_client)" />
                </FormSelect>
              </FormGroup>

              <FormGroup label="Key algorithm" fieldId="create-certificate-key-algo">
                <FormSelect
                  id="create-certificate-key-algo"
                  value={formState.keyAlgorithm}
                  onChange={(_event, value) => patch('keyAlgorithm', value as KeyAlgorithm)}
                  aria-label="Key algorithm"
                >
                  <FormSelectOption value="ec" label="Elliptic Curve" />
                  <FormSelectOption value="rsa" label="RSA" />
                </FormSelect>
                <FormHelperText>
                  <HelperText>
                    <HelperTextItem>
                      Elliptic Curve keys are smaller and faster. Some older systems may not support them — choose RSA
                      if you need broad compatibility.
                    </HelperTextItem>
                  </HelperText>
                </FormHelperText>
              </FormGroup>

              {formState.keyAlgorithm === 'ec' ? (
                <FormGroup label="EC curve" fieldId="create-certificate-ec-curve">
                  <FormSelect
                    id="create-certificate-ec-curve"
                    value={formState.ecCurve}
                    onChange={(_event, value) => patch('ecCurve', value as EcCurve)}
                    aria-label="EC curve"
                  >
                    <FormSelectOption value="prime256v1" label="prime256v1 (P-256)" />
                    <FormSelectOption value="secp384r1" label="secp384r1 (P-384)" />
                    <FormSelectOption value="secp521r1" label="secp521r1 (P-521)" />
                  </FormSelect>
                </FormGroup>
              ) : (
                <FormGroup label="RSA key size" fieldId="create-certificate-rsa-size">
                  <FormSelect
                    id="create-certificate-rsa-size"
                    value={formState.rsaSize}
                    onChange={(_event, value) => patch('rsaSize', value as RsaSize)}
                    aria-label="RSA key size"
                  >
                    <FormSelectOption value="2048" label="2048" />
                    <FormSelectOption value="3072" label="3072" />
                    <FormSelectOption value="4096" label="4096" />
                    <FormSelectOption value="8192" label="8192" />
                  </FormSelect>
                </FormGroup>
              )}

              <FormGroup label="Digest" fieldId="create-certificate-digest">
                <FormSelect
                  id="create-certificate-digest"
                  value={formState.digest}
                  onChange={(_event, value) => patch('digest', value as Digest)}
                  aria-label="Digest"
                >
                  <FormSelectOption value="sha256" label="SHA-256" />
                  <FormSelectOption value="sha384" label="SHA-384" />
                  <FormSelectOption value="sha512" label="SHA-512" />
                </FormSelect>
              </FormGroup>
            </Form>
          </ExpandableSection>

          <ExpandableSection
            toggleText="Distinguished name"
            isExpanded={isDnExpanded}
            onToggle={(_event, expanded) => setIsDnExpanded(expanded)}
          >
            <Form>
              <FormGroup label="State" fieldId="create-certificate-state">
                <TextInput
                  id="create-certificate-state"
                  type="text"
                  autoComplete="off"
                  value={formState.state}
                  onChange={(_event, value) => patch('state', value)}
                  aria-label="State"
                />
              </FormGroup>
              <FormGroup label="City" fieldId="create-certificate-city">
                <TextInput
                  id="create-certificate-city"
                  type="text"
                  autoComplete="off"
                  value={formState.city}
                  onChange={(_event, value) => patch('city', value)}
                  aria-label="City"
                />
              </FormGroup>
              <FormGroup label="Organization" fieldId="create-certificate-org">
                <TextInput
                  id="create-certificate-org"
                  type="text"
                  autoComplete="off"
                  value={formState.organization}
                  onChange={(_event, value) => patch('organization', value)}
                  aria-label="Organization"
                />
              </FormGroup>
              <FormGroup label="Organizational unit" fieldId="create-certificate-ou">
                <TextInput
                  id="create-certificate-ou"
                  type="text"
                  autoComplete="off"
                  value={formState.organizationalUnit}
                  onChange={(_event, value) => patch('organizationalUnit', value)}
                  aria-label="Organizational unit"
                />
              </FormGroup>
              <FormGroup label="Email" fieldId="create-certificate-email">
                <TextInput
                  id="create-certificate-email"
                  type="email"
                  autoComplete="off"
                  value={formState.email}
                  onChange={(_event, value) => patch('email', value)}
                  validated={emailInvalid ? 'error' : 'default'}
                  aria-label="Email"
                />
                {emailInvalid && (
                  <FormHelperText>
                    <HelperText>
                      <HelperTextItem variant="error">Enter a valid email address.</HelperTextItem>
                    </HelperText>
                  </FormHelperText>
                )}
              </FormGroup>
            </Form>
          </ExpandableSection>

          <ExpandableSection
            toggleText="Alternative names"
            isExpanded={isSansExpanded}
            onToggle={(_event, expanded) => setIsSansExpanded(expanded)}
          >
            <Form>
              <ChipListField
                label="DNS names"
                fieldId="create-certificate-sans-dns"
                values={formState.sansDns}
                onChange={(next) => patch('sansDns', next)}
                placeholder="Add a DNS name and press Enter"
                validateEntry={(value) => (value.trim().length === 0 ? 'Enter a DNS name' : null)}
              />
              <ChipListField
                label="IP addresses"
                fieldId="create-certificate-sans-ip"
                values={formState.sansIp}
                onChange={(next) => patch('sansIp', next)}
                placeholder="Add an IP and press Enter"
                validateEntry={(value) =>
                  looksLikeIp(value.trim()) ? null : `${value} is not a valid IPv4 or IPv6 address`
                }
              />
              <ChipListField
                label="URIs"
                fieldId="create-certificate-sans-uri"
                values={formState.sansUri}
                onChange={(next) => patch('sansUri', next)}
                placeholder="Add a URI and press Enter"
                validateEntry={(value) => (value.trim().length === 0 ? 'Enter a URI' : null)}
              />
              <ChipListField
                label="Email addresses"
                fieldId="create-certificate-sans-email"
                values={formState.sansEmail}
                onChange={(next) => patch('sansEmail', next)}
                placeholder="Add an email and press Enter"
                validateEntry={(value) =>
                  EMAIL_RE.test(value.trim()) ? null : `${value} is not a valid email address`
                }
              />
            </Form>
          </ExpandableSection>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          onClick={() => void handleSubmit()}
          isLoading={isSubmitting}
          isDisabled={isSubmitting || hasBlockingValidationError}
        >
          Create certificate
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isSubmitting}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default CreateCertificateModal;
